// SNIGJ - Reset Password Page
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Lock, Sun, Moon, Globe, AlertCircle, ArrowLeft, CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const { language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await api.get(`/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setTokenEmail(response.data.email || '');
      } catch (err) {
        setTokenValid(false);
        setError(err.response?.data?.detail || (language === 'fr' ? 'Token invalide ou expiré' : 'Invalid or expired token'));
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, language]);

  const validatePassword = () => {
    if (formData.password.length < 8) {
      return language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères' 
        : 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      return language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: formData.password
      });
      
      setSuccess(true);
      toast.success(language === 'fr' 
        ? 'Mot de passe réinitialisé avec succès' 
        : 'Password reset successfully');
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' 
        ? 'Erreur lors de la réinitialisation' 
        : 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Vérification du token...' : 'Verifying token...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="flex justify-between items-center gap-3 p-4">
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">
              {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateLanguage(language === 'fr' ? 'en' : 'fr')}
              data-testid="reset-lang-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="reset-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center py-6">
          <div className="w-16 h-16 rounded bg-yellow-400 flex items-center justify-center">
            <Scale className="w-10 h-10 text-slate-900" />
          </div>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-start justify-center px-4 sm:px-8">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl sm:text-2xl">
                {language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password'}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {tokenValid 
                  ? (language === 'fr' 
                      ? 'Créez un nouveau mot de passe sécurisé' 
                      : 'Create a new secure password')
                  : (language === 'fr'
                      ? 'Le lien de réinitialisation est invalide'
                      : 'The reset link is invalid')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-2">
              {!token || !tokenValid ? (
                // Invalid or missing token
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">
                      {language === 'fr' ? 'Lien invalide' : 'Invalid Link'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {language === 'fr' 
                        ? 'Ce lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.'
                        : 'This reset link is invalid or has expired. Please request a new link.'}
                    </p>
                  </div>
                  <div className="pt-4">
                    <Link to="/forgot-password">
                      <Button className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
                        {language === 'fr' ? 'Demander un nouveau lien' : 'Request New Link'}
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : success ? (
                // Success state
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">
                      {language === 'fr' ? 'Mot de passe réinitialisé !' : 'Password Reset!'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {language === 'fr' 
                        ? 'Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.'
                        : 'Your password has been changed successfully. You can now login with your new password.'}
                    </p>
                  </div>
                  <div className="pt-4">
                    <Link to="/login">
                      <Button className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
                        {language === 'fr' ? 'Se connecter' : 'Login'}
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                // Reset form
                <>
                  {tokenEmail && (
                    <div className="mb-4 p-3 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 text-sm">
                      {language === 'fr' ? 'Réinitialisation pour : ' : 'Resetting password for: '}
                      <strong>{tokenEmail}</strong>
                    </div>
                  )}

                  {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          minLength={8}
                          data-testid="reset-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' ? 'Minimum 8 caractères' : 'Minimum 8 characters'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          required
                          data-testid="reset-confirm-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                      disabled={loading}
                      data-testid="reset-submit"
                    >
                      {loading 
                        ? (language === 'fr' ? 'Réinitialisation...' : 'Resetting...') 
                        : (language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password')}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} GROUPE KAZAM
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
